/**
 * Located-vessel glyph decisions — the rules that decide how a hull found by
 * search or lookup is drawn, extracted pure from `VoyageMapController`
 * (phase 1, REFACTOR-PLAN.md §7).
 *
 * These are AIS-domain rules, not one provider's: which of COG/heading counts
 * as a real direction, when a fix is too old to wear the authority of a
 * current one, and what the marker's markup says about each. What did NOT move
 * here is `toLocatedRecord` — that normalises Voyage Radar's own payload shape
 * (`identity`/`asOf`/`time_utc`) and is gateway material (§A.5), not shared
 * vocabulary.
 *
 * Inputs are STRUCTURAL (`{ cog?, heading?, timeUtc? }`) rather than a record
 * type imported from a provider folder — that is rule R5 holding, not an
 * accident. Pure model file: no `@angular/*`, no `leaflet` (rule R1); the
 * caller wraps `locatedIconSpec`'s return in `L.divIcon(...)`.
 */

/** A fix older than this is drawn hollow and captioned "last known". */
export const LOCATED_STALE_FIX_MS = 48 * 3600 * 1000;

/**
 * True when a fix is too old to be captioned as current — or has no time at
 * all, which must never wear the authority of a fresh one.
 *
 * `nowMs` is injectable for tests, defaulting to the clock (the same idiom as
 * `hasVoyageToiFinished` in voyage-credits.model.ts).
 */
export function isStaleFix(
  timeUtc: string | null | undefined,
  nowMs: number = Date.now(),
): boolean {
  if (!timeUtc) return true;
  const t = new Date(timeUtc).getTime();
  return isNaN(t) || nowMs - t > LOCATED_STALE_FIX_MS;
}

/**
 * Which way a located hull is pointing, or null when it is not saying.
 *
 * Course over ground first — that is what the snapshot's own arrows are
 * rotated by (`displayDirectionInMap` passes `Course`), so one hull drawn by
 * both layers cannot end up with two glyphs disagreeing. Heading is the
 * fallback for a vessel making no way: moored, COG is noise or absent, but
 * the bow still points somewhere.
 *
 * `>= 360` excludes both AIS sentinels without naming them twice — 360 for
 * "course not available", 511 for "heading not available".
 */
export function locatedCourse(
  record: { cog?: any; heading?: any } | null | undefined,
): number | null {
  const real = (v: any): number | null => {
    // The empty-string guard is load-bearing: `Number('')` is 0, which would
    // point a hull that reported nothing due north.
    if (v == null || (typeof v === 'string' && !v.trim())) return null;
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 && n < 360 ? n : null;
  };
  return real(record?.cog) ?? real(record?.heading);
}

/** Everything `L.divIcon` needs — built pure so the decisions are testable. */
export interface LocatedIconSpec {
  className: string;
  html: string;
  iconSize: [number, number];
  iconAnchor: [number, number];
}

/**
 * Amber arrow along the hull's course; amber diamond when it reports none.
 * Gray for a "last known" fix. `ring` drops the glyph and keeps the halo —
 * see `paintLocated` in the map controller.
 *
 * Two shapes, and the difference between them is information: a lookup pays
 * for the course along with the fix, and drawing every hull as a diamond
 * threw it away — on a POI task, where the snapshot layer is empty and these
 * are the ONLY vessels on the chart, it threw away the whole of it. The
 * diamond stays for hulls that report no course, because an arrow pointing
 * north on a vessel that never said so is a made-up fact, not a default.
 *
 * What separates these from the snapshot's arrows is unchanged, and was never
 * the shape: colour there means ship type, here it is one constant amber, and
 * only these carry the halo.
 */
export function locatedIconSpec(
  record: { cog?: any; heading?: any; timeUtc?: string | null } | null | undefined,
  ring: boolean,
  nowMs: number = Date.now(),
): LocatedIconSpec {
  const stale = isStaleFix(record?.timeUtc, nowMs);
  const cls = `vr-located${stale ? ' vr-located--stale' : ''}${ring ? ' vr-located--ring' : ''}`;
  const course = locatedCourse(record);
  // Rotation is inline because it is per-hull: a stylesheet cannot carry 360
  // of these, and `transform` here also owns the centring both glyphs need.
  const glyph =
    course != null
      ? `<span class="vr-located__arrow" style="transform:translate(-50%,-50%) rotate(${course}deg)"></span>`
      : `<span class="vr-located__diamond"></span>`;
  return {
    className: cls,
    html: ring
      ? `<span class="vr-located__halo"></span>`
      : `<span class="vr-located__halo"></span>${glyph}`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  };
}

/**
 * Amber — the located marker family's colour, distinct from every fleet hue.
 * Moved here from the map controller (phase 4, located commit): the layer
 * paints markers with it, and the facade builds located SELECTIONS with it,
 * so the constant belongs with the glyph rules rather than with either caller.
 */
export const LOCATED_COLOR = '#B45309';
